import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationStore } from '../../stores/useNotificationStore';
import { formatDateTimeStampToDDMMMYYYYhhmm } from '../../utils/dateFormatter';
import {
  useNotificationHistory,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useUnreadNotificationCount,
} from '../../utils/hooks/useNotifications';
import { resolveNotificationRoute } from '../../utils/notificationActionResolver';
import { Loader } from '../../uikit/loader';
const NotificationDropdown = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('All');
  const dropdownRef = useRef(null);

  const notifications = useNotificationStore((state) => state.notifications);
  const { mutate: markAsRead } = useMarkNotificationRead();
  const { mutate: markAllAsRead } = useMarkAllNotificationsRead();
  const { isLoading } = useNotificationHistory();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data: unreadCount = 0 } = useUnreadNotificationCount();

  const filteredNotifications = notifications.filter((n) => {
    if (activeTab === 'Unread') return !n.isRead;
    return true;
  });

  // Show only the latest 10 in the dropdown
  const visibleNotifications = filteredNotifications.slice(0, 10);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-all duration-300"
      >
        <BellIcon className="w-6 h-6 text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 flex items-center justify-center w-4 h-4 text-[9px] font-bold text-white bg-[#ff5c00] rounded-full ring-2 ring-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Container */}
      {open && (
        <div className="absolute right-0 z-50 mt-3 w-80 bg-white rounded-xl shadow-xl border border-gray-200 transition-all duration-300 transform origin-top-right overflow-hidden">
          {/* Header */}
          <div className="bg-[#3b4b5e] px-5 py-4 flex items-center justify-between">
            <h3 className="text-base font-bold text-white">Notifications</h3>
            <button
              onClick={() => {
                setOpen(false);
                navigate('/notification-settings');
              }}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white/80 hover:text-white"
              title="Notification Settings"
            >
              <CogIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-100">
            {['All', 'Unread'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-3 text-sm font-semibold transition-all duration-200 ${
                  activeTab === tab
                    ? 'text-[#ff5c00] bg-orange-50/50 border-b-2 border-[#ff5c00]'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tab}
                {tab === 'Unread' && unreadCount > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-[9px] font-bold bg-[#ff5c00] text-white rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Notification List */}
          <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
            {isLoading ? (
              <Loader />
            ) : visibleNotifications.length > 0 ? (
              visibleNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onRead={() => markAsRead(notification.id)}
                  onNavigate={() => setOpen(false)}
                />
              ))
            ) : (
              <div className="p-10 text-center text-gray-400">
                <svg
                  className="w-12 h-12 mx-auto text-gray-200 mb-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
                <p className="text-sm">No notifications yet</p>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="p-4 border-t border-gray-100 bg-gray-50/30 text-center space-y-3">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-sm font-semibold text-gray-500 hover:text-gray-800 transition-colors"
              >
                Mark all as read
              </button>
            )}
            <button
              onClick={() => {
                setOpen(false);
                navigate('/notifications');
              }}
              className="w-full text-sm font-bold text-[#ff5c00] hover:text-[#e65300] transition-colors flex items-center justify-center gap-2 group"
            >
              View All Notifications
              <svg
                className="w-4 h-4 transform transition-transform group-hover:translate-x-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const NotificationItem = ({ notification, onRead, onNavigate }) => {
  const navigate = useNavigate();
  const { title, message, isRead, createdAt, data } = notification;

  const handleClick = () => {
    if (!isRead) {
      onRead();
    }
    if (!notification.canRedirect) return;
    const route = resolveNotificationRoute(data?.action_type, data?.params);
    if (route) {
      if (onNavigate) onNavigate();
      navigate(route);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`relative flex items-start gap-4 p-4 border-b border-gray-50 hover:bg-[#f8faff] transition-colors group cursor-pointer ${
        !isRead ? 'bg-[#f8faff]' : 'bg-white'
      }`}
    >
      <div className="p-2.5 rounded-xl shrink-0 bg-[#fff0e6] transition-transform group-hover:scale-105">
        <BellIcon className="w-5 h-5 text-[#ff5c00]" />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm leading-tight ${
            !isRead ? 'font-bold text-gray-900' : 'font-medium text-gray-700'
          }`}
        >
          {title}
        </p>
        {message && (
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{message}</p>
        )}
        <p className="text-xs text-gray-400 mt-1.5 font-medium">
          {formatDateTimeStampToDDMMMYYYYhhmm(createdAt)}
        </p>
      </div>
      {!isRead && (
        <div className="w-2.5 h-2.5 rounded-full bg-[#3b82f6] mt-2 shrink-0 shadow-sm" />
      )}
    </div>
  );
};

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
    message: PropTypes.string,
    isRead: PropTypes.bool,
    createdAt: PropTypes.string,
    canRedirect: PropTypes.bool,
    data: PropTypes.shape({
      action_type: PropTypes.string,
      params: PropTypes.string,
    }),
  }),
  onRead: PropTypes.func,
  onNavigate: PropTypes.func,
};

const BellIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </svg>
);

BellIcon.propTypes = { className: PropTypes.string };

const CogIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

CogIcon.propTypes = { className: PropTypes.string };

export default NotificationDropdown;
